import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import AdminLayout from '@/components/feature/AdminLayout';

interface Stats {
  totalProducts: number;
  totalInquiries: number;
  newInquiries: number;
  totalArticles: number;
}

interface RecentInquiry {
  id: string;
  name: string;
  email: string;
  product_interest: string;
  status: string;
  created_at: string;
}

interface RecentProduct {
  id: string;
  name: string;
  category: string;
  origin: string;
  created_at: string;
}

export default function AdminDashboardPage() {
  const [stats, setStats] = useState<Stats>({ totalProducts: 0, totalInquiries: 0, newInquiries: 0, totalArticles: 0 });
  const [recentInquiries, setRecentInquiries] = useState<RecentInquiry[]>([]);
  const [recentProducts, setRecentProducts] = useState<RecentProduct[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      try {
        const [productsRes, inquiriesRes, newInquiriesRes, articlesRes, recentInqRes, recentProdRes] = await Promise.all([
          supabase.from('products').select('id', { count: 'exact', head: true }),
          supabase.from('inquiries').select('id', { count: 'exact', head: true }),
          supabase.from('inquiries').select('id', { count: 'exact', head: true }).eq('status', 'new'),
          supabase.from('articles').select('id', { count: 'exact', head: true }),
          supabase.from('inquiries').select('id,name,email,product_interest,status,created_at').order('created_at', { ascending: false }).limit(5),
          supabase.from('products').select('id,name,category,origin,created_at').order('created_at', { ascending: false }).limit(5),
        ]);

        if (cancelled) return;

        setStats({
          totalProducts: productsRes.count || 0,
          totalInquiries: inquiriesRes.count || 0,
          newInquiries: newInquiriesRes.count || 0,
          totalArticles: articlesRes.count || 0,
        });
        setRecentInquiries((recentInqRes.data || []) as RecentInquiry[]);
        setRecentProducts((recentProdRes.data || []) as RecentProduct[]);
      } catch (err) {
        console.error('Failed to load dashboard data:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchData();
    return () => { cancelled = true; };
  }, []);

  const statCards = [
    { label: 'Total Products', value: stats.totalProducts, icon: 'ri-shopping-bag-3-line', color: 'bg-primary-500', link: '/admin/products' },
    { label: 'Total Inquiries', value: stats.totalInquiries, icon: 'ri-mail-line', color: 'bg-accent-500', link: '/admin/inquiries' },
    { label: 'New Inquiries', value: stats.newInquiries, icon: 'ri-message-3-line', color: 'bg-green-500', link: '/admin/inquiries' },
    { label: 'Articles', value: stats.totalArticles, icon: 'ri-article-line', color: 'bg-secondary-500', link: '/admin/articles' },
  ];

  return (
    <AdminLayout>
      <div className="space-y-8">
        <div>
          <h1 className="text-2xl font-heading font-bold text-foreground-900">Dashboard Overview</h1>
          <p className="text-sm text-foreground-500 mt-1">Welcome back, here&apos;s what&apos;s happening with your catalogue.</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {statCards.map((card) => (
                <Link
                  key={card.label}
                  to={card.link}
                  className="bg-background-50 border border-background-200 rounded-xl p-5 hover:border-background-300 transition-colors cursor-pointer group"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-xs text-foreground-500 font-medium uppercase tracking-wider">{card.label}</p>
                      <p className="text-3xl font-heading font-bold text-foreground-900 mt-1">{card.value}</p>
                    </div>
                    <div className={`w-10 h-10 ${card.color} rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform`}>
                      <i className={`${card.icon} text-lg text-background-50`} />
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-background-50 border border-background-200 rounded-xl p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-heading font-semibold text-foreground-900">Recent Inquiries</h3>
                  <Link to="/admin/inquiries" className="text-xs text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap cursor-pointer">
                    View All
                  </Link>
                </div>
                {recentInquiries.length === 0 ? (
                  <p className="text-sm text-foreground-500 py-4">No inquiries yet.</p>
                ) : (
                  <div className="space-y-3">
                    {recentInquiries.map((inq) => (
                      <div key={inq.id} className="flex items-center justify-between py-2 border-b border-background-100 last:border-0">
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-foreground-900 truncate">{inq.name}</p>
                          <p className="text-xs text-foreground-500 truncate">{inq.product_interest || inq.email}</p>
                        </div>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap ml-3 ${
                          inq.status === 'new' ? 'bg-green-100 text-green-700' :
                          inq.status === 'read' ? 'bg-secondary-100 text-secondary-700' :
                          inq.status === 'replied' ? 'bg-accent-100 text-accent-700' :
                          'bg-foreground-100 text-foreground-500'
                        }`}>
                          {inq.status}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="bg-background-50 border border-background-200 rounded-xl p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-heading font-semibold text-foreground-900">Recent Products</h3>
                  <Link to="/admin/products" className="text-xs text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap cursor-pointer">
                    View All
                  </Link>
                </div>
                {recentProducts.length === 0 ? (
                  <p className="text-sm text-foreground-500 py-4">No products yet.</p>
                ) : (
                  <div className="space-y-3">
                    {recentProducts.map((prod) => (
                      <div key={prod.id} className="flex items-center justify-between py-2 border-b border-background-100 last:border-0">
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-foreground-900 truncate">{prod.name}</p>
                          <p className="text-xs text-foreground-500 truncate">{prod.category} · {prod.origin}</p>
                        </div>
                        <span className="text-xs text-foreground-400 whitespace-nowrap ml-3">
                          {new Date(prod.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
}